import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface Message {
  id: string;
  chat_id: string;
  sender_id: string;
  content: string;
  is_read: boolean;
  audio_url: string | null;
  image_url: string | null;
  created_at: string;
}

interface Chat {
  id: string;
  user1_id: string;
  user2_id: string;
  last_message_at: string | null;
  created_at: string;
  otherUser?: any;
  lastMessage?: Message;
}

export const useChats = () => {
  const { user } = useAuthContext();
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchChats = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    const { data, error } = await supabase
      .from("chats")
      .select(`
        *,
        user1:profiles!chats_user1_id_fkey(*),
        user2:profiles!chats_user2_id_fkey(*)
      `)
      .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
      .order("last_message_at", { ascending: false, nullsFirst: false });

    if (!error && data) {
      const transformedChats = data.map((chat) => ({
        ...chat,
        otherUser: chat.user1_id === user.id ? chat.user2 : chat.user1,
      }));
      setChats(transformedChats);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  // Subscribe to new messages to update chat list
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("chats-updates")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "chats",
        },
        () => {
          fetchChats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchChats]);

  return { chats, loading, refetch: fetchChats };
};

export const useMessages = (chatId: string | null) => {
  const { user } = useAuthContext();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = useCallback(async () => {
    if (!chatId) return;

    setLoading(true);
    const { data, error } = await supabase
      .from("messages")
      .select("*")
      .eq("chat_id", chatId)
      .order("created_at", { ascending: true });

    if (!error && data) {
      setMessages(data);
    }
    setLoading(false);
  }, [chatId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Subscribe to new messages in this chat
  useEffect(() => {
    if (!chatId) return;

    const channel = supabase
      .channel(`messages-${chatId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `chat_id=eq.${chatId}`,
        },
        (payload) => {
          setMessages((prev) => [...prev, payload.new as Message]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatId]);

  const sendMessage = useCallback(async (content: string) => {
    if (!user || !chatId) return { error: new Error("Not authenticated or no chat") };

    const { error } = await supabase
      .from("messages")
      .insert({
        chat_id: chatId,
        sender_id: user.id,
        content,
      });

    return { error };
  }, [user, chatId]);

  const markAsRead = useCallback(async () => {
    if (!user || !chatId) return;

    await supabase
      .from("messages")
      .update({ is_read: true })
      .eq("chat_id", chatId)
      .neq("sender_id", user.id)
      .eq("is_read", false);
  }, [user, chatId]);

  return { messages, loading, sendMessage, markAsRead, refetch: fetchMessages };
};
